import type { Connection, Signer, Transaction } from "@solana/web3.js";
import type { ReactNode } from "react";

import { confirmOptions } from "../../constants/transaction";
import type { MultiTxnNotificationStrings } from "../../contexts/language/languageTypes";
import type { ConnectedWallet } from "../../hooks/wallet/useConnectedWallet";
import { notify } from "../notifications";
import { formatStringWithPlaceholders } from "../utils";
import { sendPostSendTxnErrorContent } from ".";
import { sendMultiTxNotification } from "./notifications";

export type SendAllTransactionsResult = { txid: string; state: boolean }[];

type NotificationOptions = {
  txnPendingMessage?: string | ReactNode[];
  txnSuccessMessage?: string | ReactNode[];
  onTransactionInProgress?: () => void;
  onSomeTransactionsConfirmed?: (txnResults: SendAllTransactionsResult) => void;
  onAllTransactionsConfirmed?: () => void;
};

export const sendAllTransactions = async (
  connection: Connection,
  wallet: ConnectedWallet,
  transactions: Transaction[],
  signers: Signer[][],
  transactionNotificationOptions: NotificationOptions,
  options = confirmOptions,
  notificationStrings: MultiTxnNotificationStrings,
): Promise<SendAllTransactionsResult> => {
  const {
    txnPendingMessage,
    txnSuccessMessage,
    onAllTransactionsConfirmed,
    onTransactionInProgress,
    onSomeTransactionsConfirmed,
  } = transactionNotificationOptions;
  const recentBlockhash = (await connection.getLatestBlockhash("confirmed")).blockhash;
  transactions.forEach((t, index) => {
    t.recentBlockhash = recentBlockhash;
    // fee paid by the wallet owner
    t.feePayer = wallet.publicKey;
    if (signers[index] && signers[index].length > 0) {
      t.partialSign(...signers[index]);
    }
  });

  notify({
    type: "open",
    message: notificationStrings.approveTransaction,
  });

  transactions = (await wallet.signAllTransactions(transactions)) as unknown as Transaction[];
  const rawTransactions = transactions.map((t) => t.serialize());

  if (onTransactionInProgress) {
    onTransactionInProgress();
  }

  const notifyKey = Math.random().toString(36);
  const allTxs: string[] = [];
  const processedTxs: string[] = [];
  const txConfirmationStates: boolean[] = [];

  for (let idx = 0; idx < rawTransactions.length; idx++) {
    const rawTx = rawTransactions[idx];
    const txid = await connection.sendRawTransaction(rawTx, options);
    allTxs.push(txid);
    processedTxs.push(txid);
    sendMultiTxNotification(
      processedTxs,
      txConfirmationStates,
      {
        message:
          txnPendingMessage ??
          formatStringWithPlaceholders(
            notificationStrings.swapTransactionSplit.inProgress,
            idx + 1,
            rawTransactions.length,
          ),
        key: notifyKey,
        duration: 0,
        type: "open",
      },
      notificationStrings,
    );

    try {
      const status = (await connection.confirmTransaction(txid, options.commitment)).value;
      const response = await connection.getTransaction(txid, {
        commitment: "confirmed",
        maxSupportedTransactionVersion: 2,
      });
      if (status?.err) {
        txConfirmationStates.push(false);
        if (response) {
          sendPostSendTxnErrorContent(txid, response);
        }
      } else {
        txConfirmationStates.push(true);
      }
    } catch (e) {
      txConfirmationStates.push(false);
    }
  }

  const confirmedTxs = txConfirmationStates.filter((tx) => tx).length;
  sendMultiTxNotification(
    processedTxs,
    txConfirmationStates,
    {
      message:
        txnSuccessMessage ??
        formatStringWithPlaceholders(
          notificationStrings.swapTransactionSplit.success,
          confirmedTxs,
          rawTransactions.length,
        ),
      key: notifyKey,
      duration: 5,
      type: confirmedTxs === rawTransactions.length ? "success" : "error",
    },
    notificationStrings,
  );

  const txnResults = allTxs.map((tx, index) => {
    return { txid: tx, state: txConfirmationStates[index] };
  });

  if (confirmedTxs > 0 && confirmedTxs === rawTransactions.length && onAllTransactionsConfirmed) {
    onAllTransactionsConfirmed();
  } else if (confirmedTxs > 0 && confirmedTxs !== rawTransactions.length && onSomeTransactionsConfirmed) {
    onSomeTransactionsConfirmed(txnResults);
  }

  return txnResults;
};
